import React, { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from 'react-query'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  useToaster
} from 'react-angle-dashboard-components'

import {
  useCreateGroup,
  useGroup,
  useUpdateGroup
} from '../../hooks/quote.hooks'
import {
  mapGroupDTOToForm,
  mapGroupFormToCreateDTO
} from '../../services/quote/mappers'
import { quoteService } from '../../services'

import './GroupFormModal.css'

const groupFormSchema = z.object({
  groupName: z.string().min(1, 'Group name is required'),
  zipCode: z
    .string()
    .min(1, 'Zip code is required')
    .refine((val) => {
      return val.length === 5 || val.length === 9
    }, 'Zip code must be exactly 5 or 9 characters and contain no dashes')
    .refine((val) => !!Number(val), 'Input must be a number'),
  city: z.string().min(1, 'City is required'),
  state: z.string().length(2),
  sicCode: z.string()
})

export type IGroupForm = z.infer<typeof groupFormSchema>

interface GroupFormModalProps {
  open: boolean
  onClose: () => void
  groupIdToEdit: number | undefined
}

const GroupFormModal = ({
  open,
  onClose,
  groupIdToEdit
}: GroupFormModalProps) => {
  const { data: groupToEdit } = useGroup(groupIdToEdit)
  const { showSuccess, showError } = useToaster()
  const { data: options } = useQuery(
    ['state-choices'],
    quoteService.getStateChoices,
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      retry: false,
    }
  )

  const states = useMemo(() => {
    const localStates = options?.states || []
    localStates.sort()

    return localStates
  }, [options])

  const { mutateAsync: createGroupMutate } = useCreateGroup()
  const { mutateAsync: updateGroupMutate } = useUpdateGroup()
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<IGroupForm>({
    resolver: zodResolver(groupFormSchema)
  })

  useEffect(() => {
    if (groupToEdit) {
      reset(mapGroupDTOToForm(groupToEdit))
    }
  }, [groupToEdit, reset])

  const createGroup = async (group: IGroupForm) => {
    if (!group.sicCode) {
      group.sicCode = '0000'
    }

    await createGroupMutate(mapGroupFormToCreateDTO(group))
    showSuccess('Group created successfully')
  }

  const updateGroup = async (group: IGroupForm) => {
    if (!group.sicCode) {
      group.sicCode = '0000'
    }

    if (groupIdToEdit) {
      await updateGroupMutate({
        groupId: groupIdToEdit,
        data: mapGroupFormToCreateDTO(group)
      })
    }
  }

  const onSubmit = async (group: IGroupForm) => {
    try {
      if (groupIdToEdit) {
        // edit
        await updateGroup(group)
      } else {
        await createGroup(group)
      }
      onClose()
    } catch (err) {
      console.error(err)
      showError('An error occured saving the group')
    }
  }

  return (
    <Modal
      show={open}
      centered
      onHide={onClose}
      dialogClassName="groupFormModalDialog"
      className="groupFormModal"
      style={{
        zIndex: 2000
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>{groupIdToEdit ? 'Edit Group' : 'New Group'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Group Name</Form.Label>
              <Form.Control
                isInvalid={!!errors?.groupName}
                {...register('groupName')}
              />
              <Form.Control.Feedback type="invalid">
                {errors?.groupName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>SIC Code</Form.Label>
              <Form.Control
                isInvalid={!!errors?.sicCode}
                {...register('sicCode')}
              />
              <Form.Control.Feedback type="invalid">
                {errors?.sicCode?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>Zip Code</Form.Label>
              <Form.Control
                isInvalid={!!errors?.zipCode}
                {...register('zipCode')}
              />
              <Form.Control.Feedback type="invalid">
                {errors?.zipCode?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>City</Form.Label>
              <Form.Control isInvalid={!!errors?.city} {...register('city')} />
              <Form.Control.Feedback type="invalid">
                {errors?.city?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col>
            <Form.Group>
              <Form.Label>State</Form.Label>
              <Form.Control
                as="select"
                isInvalid={!!errors?.state}
                {...register('state')}
              >
                <option value=""></option>
                {states.map((state) => (
                  <option value={state}>{state}</option>
                ))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">
                {errors?.state?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button variant="outline-primary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(onSubmit)}>
          {groupIdToEdit ? 'Edit' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default GroupFormModal
